/*
 * Copyright (c) 2024-2026, s0up and the autobrr contributors.
 * SPDX-License-Identifier: GPL-2.0-or-later
 */

import React from "react";
import { useQuery } from "@tanstack/react-query";
import { getSNMPDevice, getSNMPInterfaceHistory, SNMPDevice } from "@/api/snmp";
import { useSNMPDevice } from "@/hooks/useSNMPDevice";
import { Badge } from "@/components/ui/badge";
import { MiniAreaChart } from "@/components/common/MiniAreaChart";
import { CHART_COLORS } from "@/constants/chartColors";
import { toTrafficPoints } from "@/utils/snmpTraffic";
import { formatBytes } from "@/utils/formatBytes";
import { WidgetCard, WidgetAccent } from "./WidgetCard";

const OPER_STATUS_UP = 1;

interface SwitchPortsWidgetProps {
  deviceId: number;
}

export const SwitchPortsWidget: React.FC<SwitchPortsWidgetProps> = ({ deviceId }) => {
  const { data: device, isLoading: isLoadingDevice } = useQuery({
    queryKey: ["snmp-device", deviceId],
    queryFn: () => getSNMPDevice(deviceId),
  });

  if (isLoadingDevice || !device) {
    return (
      <WidgetCard title="Switch">
        <p className="text-sm text-gray-500 dark:text-gray-400">Loading...</p>
      </WidgetCard>
    );
  }

  // Split into an inner component so useSNMPDevice (which reads device.id
  // unconditionally) is never called with a still-loading/undefined device.
  return <SwitchPortsWidgetInner device={device} />;
};

const SwitchPortsWidgetInner: React.FC<{ device: SNMPDevice }> = ({ device }) => {
  const { interfaces = [], isLoadingInterfaces } = useSNMPDevice({ device });

  const upCount = interfaces.filter((i) => i.latest?.operStatus === OPER_STATUS_UP).length;
  const errorPorts = interfaces.filter(
    (i) =>
      (i.latest?.inErrors ?? 0) > 0 ||
      (i.latest?.outErrors ?? 0) > 0 ||
      (i.latest?.inDiscards ?? 0) > 0 ||
      (i.latest?.outDiscards ?? 0) > 0,
  ).length;

  // Chart the single busiest up interface as a representative trend --
  // a true device-level aggregate across every interface would need a new
  // backend aggregation endpoint, out of scope for this pass.
  const topInterface = interfaces
    .filter((i) => i.latest?.operStatus === OPER_STATUS_UP)
    .sort(
      (a, b) =>
        (b.latest!.inOctets + b.latest!.outOctets) -
        (a.latest!.inOctets + a.latest!.outOctets),
    )[0];

  const { data: statsHistory } = useQuery({
    queryKey: ["snmp-interface-history", device.id, topInterface?.id],
    queryFn: () => getSNMPInterfaceHistory(device.id, topInterface!.id, 1, 30),
    enabled: !!topInterface,
  });

  const trafficHistory = statsHistory
    ? toTrafficPoints(statsHistory.data).map((p) => ({ time: p.time, value: p.in + p.out }))
    : [];

  const accent: WidgetAccent = interfaces.length === 0
    ? "neutral"
    : errorPorts > 0
      ? "error"
      : upCount < interfaces.length
        ? "warn"
        : "ok";

  return (
    <WidgetCard title={device.name} accent={accent}>
      {isLoadingInterfaces ? (
        <p className="text-sm text-gray-500 dark:text-gray-400">Loading ports...</p>
      ) : (
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs text-gray-500 dark:text-gray-400">Ports up</p>
            <p className="text-lg font-semibold text-gray-900 dark:text-white">
              {upCount} / {interfaces.length}
            </p>
          </div>
          {errorPorts > 0 ? (
            <Badge variant="warning">{errorPorts} with errors</Badge>
          ) : (
            <Badge variant="success">No errors</Badge>
          )}
        </div>
      )}
      {topInterface && (
        <div className="mt-2">
          <p className="text-xs text-gray-500 dark:text-gray-400 mb-1 truncate">
            {topInterface.ifAlias || topInterface.ifDescr} (traffic)
          </p>
          <MiniAreaChart
            data={trafficHistory}
            color={CHART_COLORS.networkIn}
            gradientId={`switch-traffic-${device.id}`}
            height={70}
            valueFormatter={(v) => `${formatBytes(v)}/s`}
            emptyLabel="No history yet"
          />
        </div>
      )}
    </WidgetCard>
  );
};
